import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const SLIDES = [
  {
    id: "optimization-intake",
    name: "Optimization Program",
    role: "Your entry into the Integrated Optimization Program",
    description: "Complete one intake to get your unique member ID — the key that unlocks personalized peptides, hormones, supplements, and whole-person optimization.",
    accent: "text-brand-red",
    cta: "Start your intake",
    to: "/assistants#optimization-intake",
  },
  {
    id: "supplements",
    name: "Maya",
    role: "Supplement Optimization Specialist",
    description: "Get a free, personalized supplement analysis tailored to your wellness goals — powered by the Vantyx line.",
    accent: "text-blue-300",
    sponsor: "Powered by Vantyx",
    cta: "Chat with Maya",
    to: "/assistants#supplements",
  },
];

export function SpecialistSpotlight() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => setActive((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(timer.current);
  }, [paused]);

  const s = SLIDES[active];

  return (
    <section className="py-10 px-4 sm:px-6 lg:px-8 bg-white" data-testid="specialist-spotlight">
      <div className="max-w-7xl mx-auto">
        <div
          className="iw-grid-bg-dark relative overflow-hidden bg-[#081334] border-2 border-navy p-6 sm:p-10"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1 min-w-0">
              <p className="font-body text-[11px] font-bold uppercase tracking-widest text-brand-red mb-2">Begin Your Optimization</p>
              <div key={s.id} className="animate-in fade-in slide-in-from-bottom-2 duration-500 min-h-[150px] sm:min-h-[130px]" data-testid="spotlight-active-card">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-heading text-2xl sm:text-3xl text-white font-bold" data-testid="spotlight-name">{s.name}</h2>
                  {s.sponsor && (
                    <span className="font-mono text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-2.5 py-1" data-testid="spotlight-sponsor-badge">{s.sponsor}</span>
                  )}
                </div>
                <p className={`font-body text-sm font-semibold mt-2 ${s.accent}`} data-testid="spotlight-role">{s.role}</p>
                <p className="font-body text-sm text-white/70 mt-1.5 max-w-2xl" data-testid="spotlight-description">{s.description}</p>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <Link
                  to={s.to}
                  className="inline-flex items-center gap-2 bg-brand-red hover:bg-red-800 text-white font-mono text-[10px] uppercase font-semibold px-5 py-3 transition-colors"
                  data-testid="spotlight-chat-link"
                >
                  {s.cta} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/assistants" className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-white/80 hover:text-white transition-colors" data-testid="spotlight-all-link">
                  See the full program <ArrowRight className="w-4 h-4 text-brand-red" />
                </Link>
              </div>
            </div>

            <div className="flex md:flex-col items-center justify-between md:justify-center gap-4">
              <div className="flex md:flex-col items-center gap-2" data-testid="spotlight-dots">
                {SLIDES.map((sp, i) => (
                  <button
                    key={sp.id}
                    onClick={() => setActive(i)}
                    aria-label={`Show ${sp.name}`}
                    className={`w-3 h-3 transition-colors ${i === active ? "bg-brand-red" : "bg-white/25 hover:bg-white/50"}`}
                    data-testid={`spotlight-dot-${sp.id}`}
                  />
                ))}
              </div>
              <div className="flex md:flex-row gap-2">
                <button
                  onClick={() => setActive((i) => (i - 1 + SLIDES.length) % SLIDES.length)}
                  aria-label="Previous"
                  className="w-9 h-9 border border-white/20 hover:border-brand-red hover:bg-brand-red/10 flex items-center justify-center text-white transition-colors"
                  data-testid="spotlight-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setActive((i) => (i + 1) % SLIDES.length)}
                  aria-label="Next"
                  className="w-9 h-9 border border-white/20 hover:border-brand-red hover:bg-brand-red/10 flex items-center justify-center text-white transition-colors"
                  data-testid="spotlight-next-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
